import Check from "@mui/icons-material/Check";
import FormatListBulleted from "@mui/icons-material/FormatListBulleted";
import Home from "@mui/icons-material/Home";
import { TierIiReportStep } from "generated-graphql/graphql";
import { ReactNode } from "react";

export enum ReportStepState {
  NotStarted = "not started",
  Done = "done",
  Invalid = "invalid",
}

export type ReportStepMetadata = {
  title: string;
  route: string;
  hideOnReviewPage?: boolean;
  /** `touchedSteps` name */
  key: TierIiReportStep;
  verifiedTitle?: string;
  icon?: React.ReactNode;
  verifiedIcon?: React.ReactNode;
  getOverviewDescription: (params?: {
    isHmbp?: boolean;
    hasProducts?: boolean;
  }) => ReactNode;
};

export const reportStepMetadata: {
  [step in TierIiReportStep]: ReportStepMetadata;
} = {
  REPORT_OVERVIEW: {
    title: "Overview",
    route: "",
    key: TierIiReportStep.ReportOverview,
    getOverviewDescription: () =>
      "Review and validate information on your report.",
    verifiedTitle: "Filing Progress",
    icon: <Home />,
    verifiedIcon: <FormatListBulleted />,
  },
  FACILITY: {
    title: "Facility",
    route: "facility",
    key: TierIiReportStep.Facility,
    getOverviewDescription: () =>
      "Add or confirm the information about your facility and where it's located.",
    verifiedIcon: <Check />,
  },
  REGULATORY: {
    title: "Regulatory",
    route: "regulatory",
    key: TierIiReportStep.Regulatory,
    getOverviewDescription: ({ isHmbp } = {}) => {
      if (isHmbp) {
        return "Confirm if your facility is subject to any of the listed regulations. Depending on your selection, you may need to add an RMP, TRI, or EPA ID. Many of these questions correspond to the Business Activities section of your HMBP and help determine what additional data you need to submit.";
      }
      return "Confirm if your facility is subject to any of the listed regulations. Depending on your selection, you may need to add an RMP or TRI ID.";
    },
    verifiedIcon: <Check />,
  },
  CHEMICALS: {
    title: "Chemicals",
    route: "chemicals",
    key: TierIiReportStep.Chemicals,
    getOverviewDescription: () =>
      "Confirm or update the chemical inventory at your facility. Include all reportable chemicals present at or above the threshold amounts at your facility in the last calendar year. Check all locations and quantities and make sure they are up to date.",
  },
  PRODUCTS: {
    title: "Products",
    route: "products",
    key: TierIiReportStep.Products,
    getOverviewDescription: () => (
      <>
        Products are groups of hazardous chemicals from your chemical catalog
        that are packaged into a single item. This allows you to count units
        rather than chemical quantities. If you have chemicals outside of
        Products at your facility, those chemicals will appear in Other
        Chemicals. The total chemicals at your facility across both Products and
        Other Chemicals will appear in Chemical Summary.
        <br />
        <br />
        Confirm or update the number of products and equipment at your facility
        that contain reportable chemicals. We will calculate whether the total
        chemical quantity is at or above reportable thresholds.
      </>
    ),
  },
  OTHER_CHEMICALS: {
    title: "Other Chemicals",
    route: "other-chemicals",
    key: TierIiReportStep.OtherChemicals,
    getOverviewDescription: () =>
      "Confirm or update the chemical inventory at your facility. Include all reportable chemicals present at or above the threshold amounts at your facility in the last calendar year. Check all locations and quantities and make sure they are up to date.",
  },
  CHEMICAL_SUMMARY: {
    title: "Chemical Summary",
    route: "chemical-summary",
    key: TierIiReportStep.ChemicalSummary,
    getOverviewDescription: ({ hasProducts } = {}) => {
      if (hasProducts) {
        return (
          <>
            This shows the total chemical amounts at your facility from both
            Products and Other Chemicals.
            <br />
            <br />
            Review the total chemical amounts that will be reported by your
            facility. Make changes as necessary in the Chemicals or Products
            steps.
          </>
        );
      }
      return (
        <>
          This shows how your inventory information will appear on the Tier II
          report.
          <br />
          <br />
          Review the total chemical amounts that will be reported by your
          facility. Make changes as necessary in the Chemicals step.
        </>
      );
    },
    verifiedIcon: <Check />,
  },
  CONTACTS: {
    title: "Contacts",
    route: "contacts",
    key: TierIiReportStep.Contacts,
    getOverviewDescription: () =>
      "Add or confirm the contact list for your facility.",
    verifiedIcon: <Check />,
  },
  DOCUMENTS: {
    title: "Documents",
    route: "documents",
    key: TierIiReportStep.Documents,
    getOverviewDescription: ({ isHmbp } = {}) => {
      if (isHmbp) {
        return "Some states require site maps or various plans to accompany the reports. Confirm the attached documents for your facility and upload a site map as needed. You can also upload recommended documents in the State Info step.";
      }
      return "Some states require site maps or various plans to accompany the reports. Confirm the attached documents for your facility and upload a site map as needed.";
    },

    verifiedIcon: <Check />,
  },
  STATE_INFORMATION: {
    title: "State Info",
    route: "state-information",
    key: TierIiReportStep.StateInformation,
    getOverviewDescription: ({ isHmbp } = {}) => {
      if (isHmbp) {
        return "Because the facility is in the state of California, you are required to fill out a Hazardous Materials Business Plan (HMBP) which includes the following forms. You may be asked to fill out more information depending on the environmental activities at the facility.";
      }
      return "Some states require additional information to be included on the reports. Confirm or update the information for your facility. Many custom state fields are optional or can be calculated based on other data you have already entered, so Encamp does not display them here.";
    },

    verifiedIcon: <Check />,
  },
  REVIEW: {
    title: "Review",
    route: "review",
    hideOnReviewPage: true,
    key: TierIiReportStep.Review,
    getOverviewDescription: () =>
      "Review and validate information on your report.",
    verifiedTitle: "Reviewers & Verifiers",
    verifiedIcon: <Check />,
  },
  VERIFY_AND_SUBMIT: {
    title: "Verify & Submit",
    route: "verify-submit",
    hideOnReviewPage: true,
    key: TierIiReportStep.VerifyAndSubmit,
    getOverviewDescription: () =>
      "Verify and submit. Our team will review, process, calculate fees, and submit to state portals on your behalf.",
  },
};
